import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const RUNTIME_BASE = import.meta.env.VITE_RUNTIME_API ?? 'http://localhost:8159';

type ModuleRecord = {
  module_id: string;
  name: string;
  version: string;
  state: string;
  licensed: boolean;
  health_status?: { status: string };
};

type ModulesDashboard = {
  total_modules: number;
  compliant_modules: number;
  modules: ModuleRecord[];
};

type PlatformStatus = {
  module_id: string;
  platforms: Record<string, string>;
  overall_status: string;
};

const COMMERCE_MODULES = [
  'catalog', 'productstructure', 'productattributes', 'pricing', 'inventory',
  'listings', 'marketplacesync', 'marketplaceaccount', 'orders', 'warehouseexec',
  'logistics', 'returns', 'financecommerce', 'procurement', 'supplier', 'crm',
];

function headers() {
  return { 'X-Tenant-ID': 'default', 'X-Organization-ID': 'default', 'Content-Type': 'application/json' };
}

async function fetchJSON<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: headers() });
  if (!res.ok) throw new Error(`${res.status} ${url}`);
  return res.json() as Promise<T>;
}

export function EnterpriseModuleDashboardPage() {
  const [dashboard, setDashboard] = useState<ModulesDashboard | null>(null);
  const [selected, setSelected] = useState<string>('catalog');
  const [platformStatus, setPlatformStatus] = useState<PlatformStatus | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void fetchJSON<ModulesDashboard>(`${RUNTIME_BASE}/api/v1/modules/dashboard`)
      .then(setDashboard)
      .catch((e) => setError(String(e)));
  }, []);

  useEffect(() => {
    if (!selected) return;
    void fetchJSON<PlatformStatus>(`${RUNTIME_BASE}/api/v1/modules/${selected}/platform-status`)
      .then(setPlatformStatus)
      .catch(() => setPlatformStatus(null));
  }, [selected]);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/events" className="text-sm text-blue-600 hover:underline dark:text-blue-400">← Platform Hub</Link>
      <h1 className="mt-2 text-2xl font-semibold">Enterprise Module Dashboard</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        DEV-000055 — commerce module registry, platform integration and compliance status.
      </p>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border p-4 dark:border-slate-700">
          <p className="text-sm text-slate-500">Total modules</p>
          <p className="text-2xl font-semibold">{dashboard?.total_modules ?? COMMERCE_MODULES.length}</p>
        </div>
        <div className="rounded-xl border p-4 dark:border-slate-700">
          <p className="text-sm text-slate-500">Compliant</p>
          <p className="text-2xl font-semibold">{dashboard?.compliant_modules ?? 0}</p>
        </div>
        <div className="rounded-xl border p-4 dark:border-slate-700">
          <p className="text-sm text-slate-500">Commerce modules</p>
          <p className="text-2xl font-semibold">16</p>
        </div>
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        {COMMERCE_MODULES.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setSelected(m)}
            className={`rounded-lg px-3 py-1 text-sm ${selected === m ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'border dark:border-slate-700'}`}
          >
            {m}
          </button>
        ))}
      </div>
      {platformStatus && (
        <section className="mt-8">
          <h2 className="text-lg font-medium">Platform status — {selected}</h2>
          <p className="text-sm text-slate-500">Overall: {platformStatus.overall_status}</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(platformStatus.platforms).map(([k, v]) => (
              <div key={k} className="rounded-lg border p-3 text-sm dark:border-slate-700">
                <span className="font-medium">{k}</span>: {v}
              </div>
            ))}
          </div>
        </section>
      )}
      <section className="mt-8">
        <h2 className="text-lg font-medium">Registered modules</h2>
        <div className="mt-3 space-y-2">
          {(dashboard?.modules ?? []).map((m) => (
            <div key={m.module_id} className="rounded-lg border p-3 dark:border-slate-700">
              <p className="font-medium">{m.name ?? m.module_id}</p>
              <p className="text-sm text-slate-500">{m.state} · v{m.version} · {m.health_status?.status ?? 'unknown'}</p>
            </div>
          ))}
          {(dashboard?.modules?.length ?? 0) === 0 && (
            <p className="text-sm text-slate-500">Modules register on startup via commerceplatform.WirePlatform.</p>
          )}
        </div>
      </section>
    </div>
  );
}
